import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { applyBrand, assetUrl, whiteContrast } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const inputCls = "mt-1 bg-[#F7F5F0] border-[#DCD8D0]";
const MAX_LOGO = 2 * 1024 * 1024;
const ZONAS = [
  ["Europe/Madrid", "Península y Baleares"], ["Atlantic/Canary", "Canarias"], ["Europe/Lisbon", "Portugal"],
  ["Europe/Andorra", "Andorra"], ["Europe/Paris", "Francia"], ["Europe/London", "Reino Unido"],
];

const GRUPOS = [
  { titulo: "Marca", campos: [
    ["brand_name", "Nombre comercial", "Lo que ven tus clientes en la web, los correos y el contrato."],
    ["website", "Web", "https://…"],
    ["guide_url", "Guía o vídeos para el cliente", "Enlace que se ofrece en el pre-checkin (opcional)."],
    ["review_url", "Enlace para dejar reseña", "Se envía al cliente tras la devolución (opcional)."],
  ] },
  { titulo: "Datos legales (contrato)", campos: [
    ["legal_name", "Razón social", ""],
    ["tax_id", "NIF / CIF", ""],
    ["address", "Domicilio", ""],
  ] },
  { titulo: "Contacto con clientes", campos: [
    ["email", "Correo", ""],
    ["phone", "Teléfono", "El que ven los clientes, también en recogidas y devoluciones."],
    ["whatsapp", "WhatsApp", "Opcional."],
    ["default_location", "Lugar de recogida por defecto", "Si el cliente no elige un punto de entrega."],
    ["remote_app_name", "App para abrir y cerrar el vehículo", "Para la recogida autónoma, p. ej. «la app del fabricante»."],
  ] },
  { titulo: "Zona e idioma", campos: [] },
  { titulo: "Avisos para ti", campos: [
    ["notify_email", "Correo para avisos", "Reservas nuevas, devoluciones… Si está vacío, el de contacto."],
    ["alert_phone", "Móvil para SMS urgentes", "Formato internacional, p. ej. +34600000000."],
  ] },
  { titulo: "Correos", campos: [
    ["mail_from_name", "Nombre del remitente", "Si está vacío, el nombre comercial."],
    ["reply_to", "Responder a", "Si está vacío, el correo de contacto."],
  ] },
];

function Campo({ k, label, ayuda, value, onChange }) {
  return (
    <div>
      <Label htmlFor={`co-${k}`}>{label}</Label>
      <Input id={`co-${k}`} className={inputCls} value={value ?? ""} onChange={onChange} data-testid={`co-${k}`} />
      {ayuda && <p className="text-xs text-[#8A8A8A] mt-1">{ayuda}</p>}
    </div>
  );
}

function Color({ k, label, value, onChange }) {
  const bajo = /^#[0-9a-fA-F]{6}$/.test(value || "") && whiteContrast(value) < 4.5;
  return (
    <div>
      <Label htmlFor={`co-${k}`}>{label}</Label>
      <div className="mt-1 flex items-center gap-3">
        <input id={`co-${k}`} type="color" value={value} onChange={onChange} className="h-10 w-14 rounded border border-[#DCD8D0]" data-testid={`co-${k}`} />
        <span className="font-mono text-sm">{value}</span>
        <span className="rounded-md px-3 py-1.5 text-sm text-white" style={{ backgroundColor: value }}>Botón</span>
      </div>
      {bajo && <p className="text-xs text-[#9C6512] mt-1">El texto blanco se lee mal sobre este color; elige uno más oscuro.</p>}
    </div>
  );
}

export default function CompanySettings() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/admin/company").then(({ data }) => setC(data)).catch((e) => toast.error(formatError(e)));
  }, []);

  const set = (k) => (e) => setC((d) => ({ ...d, [k]: e.target.value }));

  const subirLogo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("El logo tiene que ser PNG, JPG o WebP");
    if (file.size > MAX_LOGO) return toast.error("El logo no puede pasar de 2 MB");
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const { data } = await api.post("/admin/uploads", { image: reader.result });
        setC((d) => ({ ...d, logo_file: data.url.split("/").pop() }));
      } catch (err) {
        toast.error(formatError(err));
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/company", c);
      setC(data);
      applyBrand({ ...data, brand_name: data.brand_name });
      toast.success("Perfil guardado");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca"><ArrowLeft className="h-4 w-4 mr-2" /> Panel</Link>
        </div>
      </header>
      <main className="container-x py-8 max-w-3xl space-y-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A]">Empresa</h1>
        {c && (
          <>
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 space-y-4" data-testid="co-logo-box">
              <h2 className="font-display text-lg font-semibold">Logo y colores</h2>
              <div className="flex items-center gap-4">
                {c.logo_file
                  ? <img src={assetUrl(`/api/files/${c.logo_file}`)} alt="Logo" className="h-16 w-auto max-w-[200px] object-contain" data-testid="co-logo-img" />
                  : <div className="h-16 w-16 rounded bg-[#EAE7E0]" />}
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm underline">
                  <Upload className="h-4 w-4" /> {uploading ? "Subiendo…" : "Cambiar logo"}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={subirLogo} data-testid="co-logo-input" />
                </label>
                {c.logo_file && <button type="button" className="text-sm text-[#5C5C5C] underline" onClick={() => setC((d) => ({ ...d, logo_file: "" }))}>Quitar</button>}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Color k="color_primary" label="Color principal" value={c.color_primary} onChange={set("color_primary")} />
                <Color k="color_accent" label="Color de acento" value={c.color_accent} onChange={set("color_accent")} />
              </div>
            </section>
            {GRUPOS.map((g) => (
              <section key={g.titulo} className="bg-white border border-[#DCD8D0] rounded-xl p-6 space-y-4">
                <h2 className="font-display text-lg font-semibold">{g.titulo}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {g.campos.map(([k, label, ayuda]) => (
                    <Campo key={k} k={k} label={label} ayuda={ayuda} value={c[k]} onChange={set(k)} />
                  ))}
                </div>
                {g.titulo === "Zona e idioma" && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="co-timezone">Zona horaria</Label>
                      <select id="co-timezone" className="mt-1 h-10 w-full rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-2 text-sm"
                        value={c.timezone} onChange={set("timezone")} data-testid="co-timezone">
                        {ZONAS.map(([z, n]) => <option key={z} value={z}>{n}</option>)}
                      </select>
                      <p className="text-xs text-[#8A8A8A] mt-1">Para recordatorios, ventanas de recogida y el «hoy» de las reservas.</p>
                    </div>
                    <div>
                      <Label htmlFor="co-lang">Idioma por defecto de la web</Label>
                      <select id="co-lang" className="mt-1 h-10 w-full rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-2 text-sm"
                        value={c.default_lang} onChange={set("default_lang")} data-testid="co-lang">
                        <option value="es">Castellano</option><option value="ca">Català</option>
                        <option value="en">English</option><option value="fr">Français</option>
                      </select>
                      <p className="text-xs text-[#8A8A8A] mt-1">Para quien llega con el navegador en un idioma que no está.</p>
                    </div>
                  </div>
                )}
                {g.titulo === "Correos" && (
                  <div className="grid gap-4">
                    <div>
                      <Label htmlFor="co-welcome_note">Nota en el correo de bienvenida</Label>
                      <Textarea id="co-welcome_note" className={inputCls} rows={3} value={c.welcome_note} onChange={set("welcome_note")} data-testid="co-welcome_note" />
                    </div>
                    <div>
                      <Label htmlFor="co-email_footer">Pie de todos los correos</Label>
                      <Textarea id="co-email_footer" className={inputCls} rows={3} value={c.email_footer} onChange={set("email_footer")} data-testid="co-email_footer" />
                      <p className="text-xs text-[#8A8A8A] mt-1">Horario, dirección, lo que quieras que lleve cada correo.</p>
                    </div>
                  </div>
                )}
              </section>
            ))}
            <Button onClick={save} disabled={saving} className="bg-marca hover:bg-marca-hover text-white" data-testid="co-save">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </>
        )}
      </main>
    </div>
  );
}
